/*
 * SIMBA — API CLIENT
 * ============================================================================
 * Mọi lời gọi mạng của giao diện learner đi qua đây: BFF của simba (/api/*),
 * gateway dùng chung api.sutucon.com (đăng nhập + affiliate), và telemetry.
 *
 * Payload được chuẩn hoá NFC ngay tại cửa: tiếng Việt gõ trên macOS về dạng tổ
 * hợp, so sánh chuỗi và tìm kiếm sẽ trượt nếu không chuẩn hoá một lần ở đây.
 */
export function normalizeUnicodeNfc<T>(value: T): T {
  if (typeof value === "string") return value.normalize("NFC") as T;
  if (Array.isArray(value))
    return value.map((item) => normalizeUnicodeNfc(item)) as T;
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        normalizeUnicodeNfc(item),
      ]),
    ) as T;
  return value;
}

export async function readApiPayload(response: Response) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    if (!response.ok)
      throw new Error(
        `Dịch vụ tạm thời không phản hồi (${response.status}). Vui lòng thử lại.`,
      );
    throw new Error("Dịch vụ trả về dữ liệu không hợp lệ.");
  }
  return normalizeUnicodeNfc(await response.json());
}

export async function api(path: string, method = "GET") {
  const response = await fetch(path, { method, credentials: "include" });
  const body: any = await readApiPayload(response);
  if (!response.ok) {
    const message =
      response.status >= 500
        ? "Simba đang gặp sự cố. Con thử lại sau nhé."
        : body.error || "Có lỗi xảy ra";
    const error: any = new Error(message);
    error.status = response.status;
    throw error;
  }
  return body.data;
}

export async function post(path: string, body: unknown) {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(normalizeUnicodeNfc(body)),
  });
  const payload: any = await readApiPayload(response);
  if (!response.ok) {
    const message =
      response.status >= 500
        ? "Simba đang gặp sự cố. Con thử lại sau nhé."
        : payload.error || "Có lỗi xảy ra";
    const error: any = new Error(message);
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

export const AFFILIATE_API = "https://api.sutucon.com";

export async function affiliateGet(path: string) {
  const response = await fetch(`${AFFILIATE_API}${path}`, {
    credentials: "include",
  });
  const payload: any = await readApiPayload(response);
  if (!response.ok) {
    const error: any = new Error(payload.error || "Có lỗi xảy ra");
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

export async function affiliatePost(path: string, body: unknown) {
  const response = await fetch(`${AFFILIATE_API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload: any = await readApiPayload(response);
  if (!response.ok) {
    const error: any = new Error(payload.error || "Có lỗi xảy ra");
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

// Login/logout are served centrally by api.sutucon.com (shared across every
// *.sutucon.com app). returnTo is an absolute simba URL; the gateway allows any
// *.sutucon.com origin and redirects back after the Google sign-in.
export const AUTH_BASE = "https://api.sutucon.com";

export const loginUrl = (returnTo = location.href) =>
  `${AUTH_BASE}/auth/login?returnTo=${encodeURIComponent(returnTo)}`;

export const logoutUrl = () =>
  `${AUTH_BASE}/auth/logout?returnTo=${encodeURIComponent(location.origin + "/")}`;

export function behaviorSessionId() {
  const key = "simba-behavior-session";
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    sessionStorage.setItem(key, value);
  }
  return value;
}

export async function trackBehavior(
  eventType: "login" | "page_view" | "course_open" | "class_open",
  path = location.pathname,
) {
  const width = window.innerWidth;
  const navigation = performance.getEntriesByType("navigation")[0] as
    PerformanceNavigationTiming | undefined;
  let referrerPath: string | undefined;
  try {
    const referrer = new URL(document.referrer);
    if (referrer.origin === location.origin) referrerPath = referrer.pathname;
  } catch {}
  await fetch("/api/telemetry", {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      event_id: crypto.randomUUID(),
      session_id: behaviorSessionId(),
      event_type: eventType,
      path,
      occurred_at: new Date().toISOString(),
      referrer_path: referrerPath,
      device_type: width < 640 ? "mobile" : width < 1024 ? "tablet" : "desktop",
      metadata: {
        viewport_bucket:
          width < 640 ? "small" : width < 1200 ? "medium" : "large",
        navigation_type: navigation?.type || "navigate",
      },
    }),
  });
}
